import { Flex, Text } from '@chakra-ui/react';
import { forwardRef } from 'react';

import {
  accent,
  controlHeight,
  font,
  fontSize,
  neutral,
  primaryAction,
  radius,
} from 'styles/revampTokens';

import FigmaScenarioBranchIcon from '../icons/FigmaScenarioBranchIcon';
import FigmaBlockDimensionsIcon from '../icons/FigmaBlockDimensionsIcon';
import FigmaSmallChevronIcon from '../icons/FigmaSmallChevronIcon';

/**
 * The header's scenario and dimensions pills, lifted out of BuilderModeHeader
 * so the dropdowns can use them as their triggers. They were the right look but
 * inert; their own components shipped the old purple/icon-only triggers, so
 * swapping one in for the other lost the revamp styling.
 *
 * Both forward refs and spread props — Chakra's PopoverTrigger and MenuButton
 * need to attach handlers and refs to the element they wrap.
 */

interface ScenarioPillProperties {
  scenarioName: string;
  [key: string]: any;
}

export const ScenarioPill = forwardRef<HTMLButtonElement, ScenarioPillProperties>(
  ({ scenarioName, ...rest }, reference) => (
    <Flex
      // Chakra's polymorphic Flex wants a ref satisfying both its own element
      // and the `as` element, which no single type does — cast rather than
      // fight the typing.
      ref={reference as any}
      as='button'
      type='button'
      alignItems='center'
      gap='8px'
      bg={neutral.surface}
      border='1px solid'
      borderColor={neutral.border}
      borderRadius={radius.control}
      h={controlHeight}
      px='12px'
      w='151px'
      cursor='pointer'
      transition='background 0.12s ease'
      _hover={{ bg: neutral.surfaceHover }}
      {...rest}
    >
      <FigmaScenarioBranchIcon color={accent.solid} />
      <Text
        flex='1'
        textAlign='left'
        fontFamily={font.body}
        fontSize={fontSize.body}
        fontWeight={500}
        color={primaryAction.bg}
        noOfLines={1}
      >
        {scenarioName}
      </Text>
      <FigmaSmallChevronIcon rotate={270} color={primaryAction.bg} />
    </Flex>
  ),
);

ScenarioPill.displayName = 'ScenarioPill';

interface DimensionsPillProperties {
  selectedDimensionCount: number;
  [key: string]: any;
}

export const DimensionsPill = forwardRef<HTMLButtonElement, DimensionsPillProperties>(
  ({ selectedDimensionCount, ...rest }, reference) => (
    <Flex
      // Chakra's polymorphic Flex wants a ref satisfying both its own element
      // and the `as` element, which no single type does — cast rather than
      // fight the typing.
      ref={reference as any}
      as='button'
      type='button'
      alignItems='center'
      gap='8px'
      bg={neutral.surface}
      border='1px solid'
      borderColor={neutral.border}
      borderRadius={radius.control}
      h={controlHeight}
      px='12px'
      cursor='pointer'
      transition='background 0.12s ease'
      _hover={{ bg: neutral.surfaceHover }}
      {...rest}
    >
      <FigmaBlockDimensionsIcon color={accent.solid} />
      <Text
        fontFamily={font.body}
        fontSize={fontSize.body}
        fontWeight={500}
        color={primaryAction.bg}
        whiteSpace='nowrap'
      >
        Block Dimensions
      </Text>
      {selectedDimensionCount > 0 && (
        <>
          <Flex w='1px' h='17px' bg={neutral.border} />
          <Text
            fontFamily={font.body}
            fontWeight={600}
            fontSize={fontSize.body}
            color={accent.solid}
            whiteSpace='nowrap'
          >
            ({selectedDimensionCount} selected)
          </Text>
          <FigmaSmallChevronIcon color={primaryAction.bg} />
        </>
      )}
    </Flex>
  ),
);

DimensionsPill.displayName = 'DimensionsPill';
